import * as React from "react";
import { useEffect, useState, Suspense } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  Paper,
  CircularProgress,
  Box,
  Alert,
} from "@mui/material";
import type { IEnvironment } from "../../../types/EnvironmentTypes";
import { FeatureItem, formatValueForInput, parseValueFromInput, isNumberValid, isJsonValid } from "../../../utils/Features";
import { FeaturesLoader, clearFeaturesCache } from "./FeaturesLoader";

interface BaseFeatureInputProps {
  feature: FeatureItem;
  currentDisplayValue: string;
  handleFeatureChange: (key: string, newValue: string) => void;
}

const BooleanFeatureInput = ({ feature, currentDisplayValue, handleFeatureChange }: BaseFeatureInputProps) => {
  return (
    <FormControl size="small" fullWidth>
      <Select value={currentDisplayValue} onChange={(e) => handleFeatureChange(feature.key, e.target.value)} displayEmpty>
        <MenuItem value="undefined">undefined</MenuItem>
        <MenuItem value="true">true</MenuItem>
        <MenuItem value="false">false</MenuItem>
      </Select>
    </FormControl>
  );
};

const NumberFeatureInput = ({ feature, currentDisplayValue, handleFeatureChange }: BaseFeatureInputProps) => {
  const isValid = isNumberValid(currentDisplayValue);
  return (
    <TextField
      size="small"
      fullWidth
      value={currentDisplayValue}
      onChange={(e) => handleFeatureChange(feature.key, e.target.value)}
      error={!isValid}
      helperText={!isValid ? "Please enter a valid number" : ""}
      placeholder="undefined"
    />
  );
};

const StringFeatureInput = ({ feature, currentDisplayValue, handleFeatureChange }: BaseFeatureInputProps) => {
  return (
    <TextField
      size="small"
      fullWidth
      value={currentDisplayValue}
      onChange={(e) => handleFeatureChange(feature.key, e.target.value)}
      placeholder="undefined"
    />
  );
};

const JsonFeatureInput = ({ feature, currentDisplayValue, handleFeatureChange }: BaseFeatureInputProps) => {
  const isValid = isJsonValid(currentDisplayValue);
  return (
    <TextField
      multiline
      rows={7}
      size="small"
      fullWidth
      value={currentDisplayValue}
      onChange={(e) => handleFeatureChange(feature.key, e.target.value)}
      error={!isValid}
      helperText={!isValid ? "Please enter a valid JSON" : ""}
      placeholder="{}"
    />
  );
};

export interface EnvironmentFeaturesPopupProps {
  visible: boolean;
  environment: IEnvironment;
  onDismiss: () => void;
  onSave: (environment: IEnvironment, changedFeatures: FeatureItem[]) => Promise<void>;
}

export const EnvironmentFeaturesPopup = ({ visible, environment, onDismiss, onSave }: EnvironmentFeaturesPopupProps) => {
  const [saving, setSaving] = useState(false);
  const [features, setFeatures] = useState<FeatureItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showHidden, setShowHidden] = useState(false);

  const displayedFeatures = showHidden ? features : features.filter((f) => !f.isHidden);

  useEffect(() => {
    if (!visible) {
      setFeatures([]);
      setError(null);
      setShowHidden(false);
      // Clear cache and promise when popup is closed
      clearFeaturesCache();
    }
  }, [visible, environment.name]);

  const handleFeaturesLoaded = (loadedFeatures: FeatureItem[]) => {
    if (features.length === 0) {
      setFeatures(loadedFeatures);
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleFeatureChange = (key: string, newValue: string) => {
    setFeatures(
      features.map((feature) => {
        if (feature.key === key) {
          const parsedValue = parseValueFromInput(newValue, feature.type);
          const hasChanged = parsedValue !== feature.originalValue;
          return {
            ...feature,
            currentValue: parsedValue,
            hasChanged,
          };
        }
        return feature;
      }),
    );
  };

  const handleSave = async () => {
    const changedFeatures = features.filter((f) => f.hasChanged);
    if (changedFeatures.length === 0) {
      onDismiss();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(environment, changedFeatures);
    } catch (error) {
      console.error("Failed to save features:", error);
      setError("Failed to save features. Please try again.");
    } finally {
      setSaving(false);
      onDismiss();
    }
  };

  const renderFeatureInput = (feature: FeatureItem) => {
    const currentDisplayValue = formatValueForInput(feature.currentValue, feature.type);
    switch (feature.type) {
      case "boolean":
        return (
          <BooleanFeatureInput
            feature={feature}
            currentDisplayValue={currentDisplayValue}
            handleFeatureChange={handleFeatureChange}
          />
        );
      case "number":
        return (
          <NumberFeatureInput
            feature={feature}
            currentDisplayValue={currentDisplayValue}
            handleFeatureChange={handleFeatureChange}
          />
        );
      case "json":
        return (
          <JsonFeatureInput
            feature={feature}
            currentDisplayValue={currentDisplayValue}
            handleFeatureChange={handleFeatureChange}
          />
        );
      case "string":
      default:
        return (
          <StringFeatureInput
            feature={feature}
            currentDisplayValue={currentDisplayValue}
            handleFeatureChange={handleFeatureChange}
          />
        );
    }
  };

  const getChangedFeaturesCount = () => {
    return features.filter((f) => f.hasChanged).length;
  };

  const hasInvalidInputs = () => {
    return features.some((feature) => {
      if (feature.type === "number") {
        const currentDisplayValue = formatValueForInput(feature.currentValue, feature.type);
        return !isNumberValid(currentDisplayValue);
      }
      if (feature.type === "json") {
        const currentDisplayValue = formatValueForInput(feature.currentValue, feature.type);
        try {
          JSON.parse(currentDisplayValue);
          return false;
        } catch {
          return true;
        }
      }
      return false;
    });
  };

  const renderContent = () => {
    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    return (
      <>
        <FormControlLabel
          control={<Checkbox checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} size="small" />}
          label="Show hidden features"
          sx={{ mb: 1 }}
        />
        <Suspense
          fallback={
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          }
        >
          <FeaturesLoader environment={environment} onFeaturesLoaded={handleFeaturesLoaded} onError={handleError} />
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {displayedFeatures.map((feature) => {
                return (
                  <Grid key={feature.key} size={{ xs: 12 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: feature.hasChanged ? "action.hover" : "transparent",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          minWidth: 200,
                          fontWeight: feature.hasChanged ? "bold" : "normal",
                          color: feature.hasChanged ? "primary.main" : "text.primary",
                        }}
                      >
                        {feature.key}:
                      </Typography>
                      <div style={{ flexGrow: 1 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                        ({feature.type})
                      </Typography>
                      <Box sx={{ flexGrow: 1, maxWidth: 300 }}>{renderFeatureInput(feature)}</Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Suspense>
      </>
    );
  };

  return (
    <Dialog open={visible} onClose={onDismiss} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Environment Features - <b>{environment.name}</b>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 400 }}>{renderContent()}</DialogContent>
      <DialogActions>
        {getChangedFeaturesCount() > 0 && (
          <Typography variant="body2" color="primary" sx={{ ml: 2 }}>
            {getChangedFeaturesCount()} feature(s) will be updated.
          </Typography>
        )}
        <div style={{ flexGrow: 1 }} />
        <Button onClick={onDismiss} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={saving || hasInvalidInputs()}>
          {saving ? (
            <CircularProgress size={20} />
          ) : (
            `Save${getChangedFeaturesCount() > 0 ? ` (${getChangedFeaturesCount()})` : ""}`
          )}
        </Button>
        <div style={{ marginRight: 8 }} />
      </DialogActions>
    </Dialog>
  );
};
